import React, { createContext, useContext, useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import { useAuth } from './AuthProvider';
import { getSocket, disconnectSocket } from '../services/socket';

interface SocketContextType {
  socket: Socket | null;
  connected: boolean;
  joinMeetup: (meetupId: string) => void;
  leaveMeetup: (meetupId: string) => void;
  sendMeetupUpdate: (meetupId: string, update: MeetupUpdatePayload) => void;
}

type MeetupUpdatePayload = Record<string, unknown>;

const SocketContext = createContext<SocketContextType | undefined>(undefined);

/** Context-bound socket helpers (meetup rooms). Prefer hooks/useSocket for event subscriptions. */
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

/**
 * Provides meetup room helpers around the canonical services/socket singleton.
 * Does not open a second Socket.IO connection.
 */
export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const { user, session } = useAuth();
  const isAuthenticated = !!user && !!session;
  const socketsEnabled = import.meta.env.VITE_ENABLE_SOCKETS !== 'false';

  useEffect(() => {
    if (!socketsEnabled || !isAuthenticated || !user) {
      disconnectSocket();
      setSocket(null);
      setConnected(false);
      return;
    }

    let active = true;
    let sock: Socket | null = null;

    const onConnect = () => {
      if (active) setConnected(true);
    };
    const onDisconnect = () => {
      if (active) setConnected(false);
    };

    (async () => {
      sock = await getSocket();
      if (!active || !sock) return;

      setSocket(sock);
      setConnected(sock.connected);

      sock.on('connect', onConnect);
      sock.on('disconnect', onDisconnect);

      sock.on('meetup:updated', (data) => {
        window.dispatchEvent(new CustomEvent('meetup:updated', { detail: data }));
      });
      sock.on('meetup:participant_joined', (data) => {
        window.dispatchEvent(new CustomEvent('meetup:participant_joined', { detail: data }));
      });
      sock.on('meetup:participant_left', (data) => {
        window.dispatchEvent(new CustomEvent('meetup:participant_left', { detail: data }));
      });
      sock.on('meetup:location_updated', (data) => {
        window.dispatchEvent(new CustomEvent('meetup:location_updated', { detail: data }));
      });
      sock.on('notification', (data) => {
        window.dispatchEvent(new CustomEvent('notification', { detail: data }));
      });
    })();

    return () => {
      active = false;
      if (sock) {
        sock.off('connect', onConnect);
        sock.off('disconnect', onDisconnect);
      }
    };
  }, [isAuthenticated, socketsEnabled, user]);

  const joinMeetup = (meetupId: string) => {
    if (socket && connected) {
      const parsedId = parseInt(meetupId, 10);
      socket.emit('join:meetup', Number.isNaN(parsedId) ? meetupId : parsedId);
    }
  };

  const leaveMeetup = (meetupId: string) => {
    if (socket && connected) {
      const parsedId = parseInt(meetupId, 10);
      socket.emit('leave:meetup', Number.isNaN(parsedId) ? meetupId : parsedId);
    }
  };

  const sendMeetupUpdate = (meetupId: string, update: MeetupUpdatePayload) => {
    if (socket && connected) {
      socket.emit('meetup:update', { meetupId, update });
    }
  };

  return (
    <SocketContext.Provider
      value={{ socket, connected, joinMeetup, leaveMeetup, sendMeetupUpdate }}
    >
      {children}
    </SocketContext.Provider>
  );
};
